import { get, type Unsubscriber } from "svelte/store";
import type { VideoBox } from "../../Space/VideoBox";
import { myCameraPeerStore } from "../../Stores/StreamableCollectionStore";
import { videoStreamStore } from "../../Stores/PeerStore";
import { roomSpatialAudioActiveStore } from "../../Stores/DystopiaRoomSpatialStore";
import {
  AVATAR_VIDEO_ENTER_RADIUS,
  AVATAR_VIDEO_EXIT_RADIUS,
  ENABLE_AVATAR_VIDEO_OVERLAYS,
} from "../../Enum/EnvironmentVariable";
import {
  extractUserIdFromSpaceUserId,
  isPointInViewport,
  resolveAvatarVideoVisibility,
  shouldUseAvatarVideoOverlays,
} from "../../WebRtc/avatar-video-visibility";
import type { RemotePlayer } from "../Entity/RemotePlayer";
import { getWokaVideoOffsetY } from "../Entity/DystopiaWokaSizing";
import { AvatarVideoDisplay } from "../Components/AvatarVideoDisplay";
import type { GameScene } from "./GameScene";

const UPDATE_INTERVAL_MS = 100;
const VIEWPORT_MARGIN = 144;

interface OverlayRecord {
  videoBox: VideoBox;
  player: RemotePlayer;
  display: AvatarVideoDisplay;
  visible: boolean;
}

export class AvatarVideoOverlayManager {
  private readonly overlays = new Map<string, OverlayRecord>();
  private videoBoxes = new Map<string, VideoBox>();
  private selfVideoBox: VideoBox | undefined;
  private selfDisplay: AvatarVideoDisplay | undefined;
  private selfDisplayVideoBox: VideoBox | undefined;
  private readonly videoStreamUnsubscriber: Unsubscriber;
  private readonly selfCameraUnsubscriber: Unsubscriber;
  private readonly interval: ReturnType<typeof setInterval>;

  private readonly updatePositions = (): void => {
    for (const overlay of this.overlays.values()) {
      overlay.display.setPosition(
        overlay.player.x,
        overlay.player.y - getWokaVideoOffsetY(overlay.player.getVisualFrameSize()),
      );
      overlay.display.setDepth(overlay.player.depth + 1);
    }

    if (this.selfDisplay) {
      this.selfDisplay.setPosition(
        this.scene.CurrentPlayer.x,
        this.scene.CurrentPlayer.y -
          getWokaVideoOffsetY(this.scene.CurrentPlayer.getVisualFrameSize()),
      );
      this.selfDisplay.setDepth(this.scene.CurrentPlayer.depth + 1);
    }
  };

  constructor(private readonly scene: GameScene) {
    this.videoStreamUnsubscriber = videoStreamStore.subscribe((videoBoxes) => {
      this.videoBoxes = new Map(videoBoxes);
      this.reconcileRemoteOverlays();
    });
    this.selfCameraUnsubscriber = myCameraPeerStore.subscribe((videoBox) => {
      this.selfVideoBox = videoBox;
      this.ensureSelfOverlay();
    });

    this.scene.events.on(Phaser.Scenes.Events.POST_UPDATE, this.updatePositions);
    this.interval = setInterval(() => this.updateVisibility(), UPDATE_INTERVAL_MS);
    this.reconcileRemoteOverlays();
    this.ensureSelfOverlay();
    this.updateVisibility();
  }

  public destroy(): void {
    this.videoStreamUnsubscriber();
    this.selfCameraUnsubscriber();
    clearInterval(this.interval);
    this.scene.events.off(Phaser.Scenes.Events.POST_UPDATE, this.updatePositions);

    for (const overlay of this.overlays.values()) {
      overlay.display.destroy();
    }
    this.overlays.clear();
    this.selfDisplay?.destroy();
    this.selfDisplay = undefined;
    this.selfDisplayVideoBox = undefined;
  }

  private reconcileRemoteOverlays(): void {
    if (!this.isActive()) {
      for (const overlay of this.overlays.values()) {
        overlay.display.destroy();
      }
      this.overlays.clear();
      return;
    }

    for (const [id, overlay] of this.overlays) {
      if (
        this.videoBoxes.get(id) !== overlay.videoBox ||
        this.findRemotePlayer(overlay.videoBox) !== overlay.player
      ) {
        overlay.display.destroy();
        this.overlays.delete(id);
      }
    }

    for (const [id, videoBox] of this.videoBoxes) {
      if (this.overlays.has(id)) continue;
      const player = this.findRemotePlayer(videoBox);
      if (!player) continue;

      this.overlays.set(id, {
        videoBox,
        player,
        display: new AvatarVideoDisplay(this.scene, videoBox, false),
        visible: false,
      });
    }
  }

  private ensureSelfOverlay(): void {
    if (!this.isActive() || !this.selfVideoBox) {
      this.selfDisplay?.destroy();
      this.selfDisplay = undefined;
      this.selfDisplayVideoBox = undefined;
      return;
    }
    if (this.selfDisplay && this.selfDisplayVideoBox !== this.selfVideoBox) {
      this.selfDisplay.destroy();
      this.selfDisplay = undefined;
      this.selfDisplayVideoBox = undefined;
    }
    if (this.selfDisplay) return;

    this.selfDisplay = new AvatarVideoDisplay(this.scene, this.selfVideoBox, true);
    this.selfDisplayVideoBox = this.selfVideoBox;
  }

  private updateVisibility(): void {
    this.reconcileRemoteOverlays();
    this.ensureSelfOverlay();

    const roomActive = this.isActive();
    const listener = this.scene.CurrentPlayer;
    const camera = this.scene.cameras.main;
    const viewport = {
      left: camera.worldView.left,
      right: camera.worldView.right,
      top: camera.worldView.top,
      bottom: camera.worldView.bottom,
    };

    for (const overlay of this.overlays.values()) {
      const streamable = get(overlay.videoBox.streamable);
      const hasVideo = streamable ? get(streamable.hasVideo) : false;
      const blocked = streamable ? get(streamable.media.isBlocked) : false;
      const connected = get(overlay.videoBox.statusStore) === "connected";
      const inViewport = isPointInViewport(overlay.player, viewport, VIEWPORT_MARGIN);
      const visible =
        roomActive &&
        resolveAvatarVideoVisibility({
          listener,
          source: overlay.player,
          previouslyVisible: overlay.visible,
          hasVideo,
          connected,
          blocked,
          inViewport,
          enterRadius: AVATAR_VIDEO_ENTER_RADIUS,
          exitRadius: AVATAR_VIDEO_EXIT_RADIUS,
        });

      overlay.visible = visible;
      overlay.display.setVisible(visible);
    }

    const selfStreamable = this.selfVideoBox ? get(this.selfVideoBox.streamable) : undefined;
    const selfHasVideo = selfStreamable ? get(selfStreamable.hasVideo) : false;
    this.selfDisplay?.setVisible(roomActive && selfHasVideo);
  }

  private isActive(): boolean {
    return shouldUseAvatarVideoOverlays(
      ENABLE_AVATAR_VIDEO_OVERLAYS,
      get(roomSpatialAudioActiveStore),
    );
  }

  private findRemotePlayer(videoBox: VideoBox): RemotePlayer | undefined {
    for (const player of this.scene.MapPlayersByKey.values()) {
      if (player.userUuid === videoBox.spaceUser.uuid) return player;
    }

    const userId = extractUserIdFromSpaceUserId(videoBox.spaceUser.spaceUserId);
    return userId === undefined ? undefined : this.scene.MapPlayersByKey.get(userId);
  }
}
